import React, { useEffect, useMemo, useRef, useState } from "react";

import { format } from "date-fns";
import { es } from "date-fns/locale";
import { CalendarIcon, Eye, PlusCircle, Trash2, Upload } from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Calendar } from "~/components/ui/calendar";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { toast } from "~/hooks/use-toast";
import { useOptimizedProfiles } from "~/hooks/useOptimizedProfiles";
import { supabase } from "~/integrations/supabase/client";
import { PaymentRecord } from "~/pages/purchase-orders/hooks/usePayments";
import { useProfileStore } from "~/store/profileStore";
import { SalesRequest } from "~/types/client";
import { formatCurrency } from "~/utils/helpers";
import { grossFromNet } from "~/utils/iva";
import {
  calculateVerifiedPayments,
  shouldMarkAsPaid,
} from "~/utils/paymentVerification";

// Chilean banks list
const CHILEAN_BANKS = [
  "Banco de Chile",
  "Banco Santander Chile",
  "BCI",
  "Banco Estado",
  "Banco BICE",
  "Banco de Crédito e Inversiones",
  "Banco Falabella",
  "Banco Security",
  "Banco Ripley",
  "Scotiabank Chile",
  "Banco Itaú Chile",
  "Banco Consorcio",
  "Banco Internacional",
  "Banco BTG Pactual Chile",
  "Banco Edwards Citi",
  "Banco Paris",
  "Banco Penta",
  "Banco Promerica",
  "Banco Coopeuch",
  "Otro",
];

// Define the database schema structure for payment table
interface PaymentDB {
  id?: number;
  sales_request?: string;
  client?: string;
  amount?: number;
  payment_method?: string;
  reference?: string;
  image_url?: string;
  tx_internal_id?: string;
  created_at?: string;
  created_by?: string;
  // Check-specific fields
  check_number?: string;
  bank?: string;
  reception_date?: string;
  collection_date?: string;
  check_status?: string;
  // Verification fields
  is_verified?: boolean | null;
  verified_by?: string;
  verified_at?: string;
}

interface AddPaymentFormProps {
  selectedOrder: SalesRequest;
  onPaymentAdded: () => Promise<void>;
  totalAmount: number;
  currentPayments: PaymentRecord[];
  onCloseOrderDialog?: () => void;
}

export const AddPaymentForm: React.FC<AddPaymentFormProps> = ({
  selectedOrder,
  onPaymentAdded,
  totalAmount,
  currentPayments,
  onCloseOrderDialog,
}) => {
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const [paymentMethod, setPaymentMethod] = useState<string>("transfer");
  const [reference, setReference] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [remainingAmount, setRemainingAmount] = useState<number>(0);
  const [file, setFile] = useState<File | null>(null);
  const [fileUploading, setFileUploading] = useState<boolean>(false);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [showPaymentForm, setShowPaymentForm] = useState<boolean>(true);
  const [deletingPaymentId, setDeletingPaymentId] = useState<number | null>(
    null
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Check-specific fields
  const [checkNumber, setCheckNumber] = useState<string>("");
  const [bank, setBank] = useState<string>("");
  const [receptionDate, setReceptionDate] = useState<Date | undefined>(
    undefined
  );
  const [collectionDate, setCollectionDate] = useState<Date | undefined>(
    undefined
  );

  // Get the current user info from the profile store
  const { id: currentUserId, role: currentUserRole } = useProfileStore();

  // Check if current user is a super admin
  const isSuperAdmin = currentUserRole === "superadmin";
  const canDeletePayments =
    currentUserRole === "superadmin" || currentUserRole === "cobranza";
  const isSeller = currentUserRole === "seller";

  // Extract all unique user IDs from payments
  const userIds = useMemo(() => {
    const ids = currentPayments
      .map((payment) => payment.createdBy)
      .filter(Boolean) as string[];
    return [...new Set(ids)];
  }, [currentPayments]);

  // Fetch user profiles
  const { getUserName } = useOptimizedProfiles(userIds);

  // Calculate remaining amount to be paid based on verified payments
  useEffect(() => {
    const calculateRemainingAmount = async () => {
      // Calculate verified payments to determine actual paid amount
      const { verifiedAmount } = await calculateVerifiedPayments(
        selectedOrder.id
      );

      // Gross (con IVA) owed; facturas exentas (SII 34) carry no IVA -> gross = net
      const totalAmountWithIva = grossFromNet(
        selectedOrder.totalAmount,
        selectedOrder.isExento
      );

      // Calculate remaining based on verified payments only
      const remaining = Math.max(0, totalAmountWithIva - verifiedAmount);
      setRemainingAmount(remaining);

      // Set default payment amount to the remaining amount
      if (remaining > 0 && paymentAmount === 0) {
        setPaymentAmount(remaining);
      }
    };

    calculateRemainingAmount();
  }, [
    currentPayments,
    selectedOrder.id,
    selectedOrder.isExento,
    selectedOrder.totalAmount,
    paymentAmount,
  ]);

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];

      // Validate file type (only images or PDFs)
      const validTypes = [
        "image/jpeg",
        "image/png",
        "image/gif",
        "application/pdf",
      ];
      if (!validTypes.includes(selectedFile.type)) {
        toast({
          title: "Formato no válido",
          description: "Por favor seleccione una imagen o PDF.",
          variant: "destructive",
        });
        return;
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (selectedFile.size > maxSize) {
        toast({
          title: "Archivo demasiado grande",
          description: "El archivo no debe superar los 5MB.",
          variant: "destructive",
        });
        return;
      }

      setFile(selectedFile);

      // Create preview for images
      if (selectedFile.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFilePreview(reader.result as string);
        };
        reader.readAsDataURL(selectedFile);
      } else {
        setFilePreview(null);
      }
    }
  };

  // Upload file to Supabase Storage
  const uploadFile = async (paymentId: number): Promise<string | null> => {
    if (!file) return null;

    try {
      setFileUploading(true);

      // Create a unique file path with payment ID and timestamp
      const fileExt = file.name.split(".").pop();
      const fileName = `${selectedOrder.id}/${paymentId}_${Date.now()}.${fileExt}`;

      // Upload the file
      const { data, error } = await supabase.storage
        .from("payment_proofs")
        .upload(fileName, file, {
          upsert: false,
          contentType: file.type,
        });

      if (error) throw error;

      // Get public URL
      const { data: publicUrlData } = supabase.storage
        .from("payment_proofs")
        .getPublicUrl(data.path);

      return publicUrlData.publicUrl;
    } catch (error) {
      console.error("Error uploading file:", error);
      toast({
        title: "Error al subir archivo",
        description: "No se pudo subir el comprobante de pago.",
        variant: "destructive",
      });
      return null;
    } finally {
      setFileUploading(false);
    }
  };

  // Open proof image in new window
  const viewPaymentProof = (imageUrl: string) => {
    window.open(imageUrl, "_blank");
  };

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    e.preventDefault();

    if (!paymentAmount || paymentAmount <= 0) {
      toast({
        title: "Error",
        description: "El monto del pago debe ser mayor a 0",
        variant: "destructive",
      });
      return;
    }

    if (paymentMethod === "transfer") {
      if (!bank) {
        toast({
          title: "Error",
          description:
            "Para transferencias bancarias, debe seleccionar un banco",
          variant: "destructive",
        });
        return;
      }
    }

    if (paymentMethod === "check") {
      if (!checkNumber || !bank || !receptionDate || !collectionDate) {
        toast({
          title: "Error",
          description:
            "Para pagos con cheque, complete todos los campos: número de cheque, banco, fecha de recepción y fecha de cobro",
          variant: "destructive",
        });
        return;
      }
    }

    setIsSubmitting(true);

    try {
      // Round payment amount to avoid centavos issues
      const roundedPaymentAmount = Math.round(paymentAmount);

      // Add payment record
      const paymentData: PaymentDB = {
        sales_request: selectedOrder.id,
        client: selectedOrder.clientId,
        amount: roundedPaymentAmount,
        payment_method: paymentMethod,
        reference: reference,
        created_at: new Date().toISOString(),
        created_by: currentUserId,
        // Set verification status: null for checks, false for all others from AddPaymentForm
        is_verified: paymentMethod === "check" ? null : false,
      };

      // Add bank field for transfers
      if (paymentMethod === "transfer" && bank) {
        paymentData.bank = bank;
      }

      // Add check-specific fields if payment method is check
      if (paymentMethod === "check") {
        paymentData.check_number = checkNumber;
        paymentData.bank = bank;
        paymentData.reception_date = receptionDate?.toISOString();
        paymentData.collection_date = collectionDate?.toISOString();

        // NEW: Set proper check status based on business logic
        // If collection date is in the future or today, check goes to 'in_portfolio' (in portfolio)
        // This indicates the check is received but not yet collected
        paymentData.check_status = "in_portfolio"; // English status value

        // Update reference to be more descriptive for checks
        paymentData.reference = `Cheque #${checkNumber} - ${bank} (En cartera hasta ${format(collectionDate, "dd/MM/yyyy")})`;
      }

      const { data: insertedPaymentData, error: paymentError } = await supabase
        .from("payments")
        .insert(paymentData)
        .select("*")
        .single();

      if (paymentError) throw paymentError;

      // Upload file if provided
      let imageUrl = null;
      if (file) {
        imageUrl = await uploadFile(insertedPaymentData.id);

        if (imageUrl) {
          // Update payment record with image URL
          const { error: updateError } = await supabase
            .from("payments")
            .update({
              image_url: imageUrl,
            } as PaymentDB)
            .eq("id", insertedPaymentData.id);

          if (updateError) {
            console.error(
              "Error updating payment with image URL:",
              updateError
            );
          }
        }
      }

      // Calculate verified payments and update paid status
      const verificationResult = await calculateVerifiedPayments(
        selectedOrder.id
      );

      // Gross (con IVA) owed; facturas exentas (SII 34) carry no IVA -> gross = net
      const totalAmountWithIva = grossFromNet(
        selectedOrder.totalAmount,
        selectedOrder.isExento
      );

      // Determine if sales request should be marked as paid based on verified payments
      const isPaidInFull = await shouldMarkAsPaid(
        selectedOrder.id,
        totalAmountWithIva
      );

      // Update sales request with new paid amounts and verification-based paid status
      const { error: updateError } = await supabase
        .from("sales_request")
        .update({
          paid_amount: verificationResult.totalPaid,
          paid: isPaidInFull, // Only true if verified payments cover total
          updated_at: new Date().toISOString(),
        })
        .eq("id", selectedOrder.id);

      if (updateError) throw updateError;

      // Show appropriate success message based on payment method and verification status
      let successMessage = "Pago registrado exitosamente";
      if (paymentMethod === "check") {
        successMessage = `Cheque #${checkNumber} registrado en cartera. El crédito se liberará cuando sea cobrado.`;
      } else if (paymentMethod === "transfer" || paymentMethod === "other") {
        successMessage =
          "Pago registrado y pendiente de verificación. Se marcará como pagado una vez verificado.";
      } else {
        successMessage = "Pago registrado y verificado automáticamente.";
      }

      toast({
        title: "Pago registrado",
        description:
          selectedOrder.status === "prepaid"
            ? "Pago registrado. La nota aparecerá en validación de pago."
            : successMessage,
      });

      // Reset form
      setPaymentAmount(0);
      setReference("");
      setFile(null);
      setFilePreview(null);
      setCheckNumber("");
      setBank("");
      setReceptionDate(undefined);
      setCollectionDate(undefined);

      // Notify parent component
      onPaymentAdded();

      // If this payment covers the full outstanding balance, return to the
      // board. Partial payments keep the dialog open so more can be added.
      if (roundedPaymentAmount >= Math.round(remainingAmount)) {
        onCloseOrderDialog?.();
      }
    } catch (error: any) {
      console.error("Error adding payment:", error);
      toast({
        title: "Error",
        description: error.message || "No se pudo registrar el pago",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete payment function
  const handleDeletePayment = async (
    paymentId: number,
    paymentImageUrl?: string
  ) => {
    const confirmed = window.confirm(
      "¿Estás seguro de que deseas eliminar este pago? Esta acción no se puede deshacer."
    );

    if (!confirmed) return;

    setDeletingPaymentId(paymentId);

    try {
      // Delete the payment from database
      const { error: deleteError } = await supabase
        .from("payments")
        .delete()
        .eq("id", paymentId);

      if (deleteError) throw deleteError;

      // If there's an image, try to delete it from storage
      if (paymentImageUrl) {
        try {
          // Extract the file path from the URL
          const urlParts = paymentImageUrl.split("/");
          const bucketIndex = urlParts.findIndex(
            (part) => part === "payment_proofs"
          );
          if (bucketIndex !== -1 && bucketIndex < urlParts.length - 1) {
            const filePath = urlParts.slice(bucketIndex + 1).join("/");

            const { error: storageError } = await supabase.storage
              .from("payment_proofs")
              .remove([filePath]);

            if (storageError) {
              console.error("Error deleting file from storage:", storageError);
              // Don't throw here, as the payment deletion was successful
            }
          }
        } catch (storageError) {
          console.error("Error deleting file from storage:", storageError);
          // Continue with success message as payment was deleted
        }
      }

      toast({
        title: "Pago eliminado",
        description: "El pago ha sido eliminado correctamente.",
      });

      // Refresh the payment data
      await onPaymentAdded();
    } catch (error: unknown) {
      console.error("Error al eliminar el pago:", error);
      toast({
        title: "Error",
        description:
          "No se pudo eliminar el pago. " +
          (error instanceof Error ? error.message : String(error)),
        variant: "destructive",
      });
    } finally {
      setDeletingPaymentId(null);
    }
  };

  return (
    <div className="border-t border-gray-200 bg-gray-50 p-5">
      {/* Display payment list with proof buttons */}
      {currentPayments && currentPayments.length > 0 && (
        <div className="mb-5">
          <h5 className="mb-2 text-sm font-medium text-gray-700">
            Pagos registrados
          </h5>
          <div className="overflow-x-scroll rounded border border-gray-200 bg-white">
            <table className="text-sm" style={{ minWidth: "800px" }}>
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                    Fecha
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">
                    Monto
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                    Método
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">
                    Verificación
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">
                    Comprobante
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">
                    Registrado por
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {currentPayments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-500">
                      {new Date(payment.createdAt).toLocaleDateString("es-ES", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right text-xs font-medium">
                      {formatCurrency(payment.amount)}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-500">
                      {payment.paymentMethod}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {payment.paymentMethod === "check" ? (
                        <Badge
                          variant="secondary"
                          className="bg-gray-100 text-gray-600"
                        >
                          Ver Estado
                        </Badge>
                      ) : payment.fintoc_movement_id || payment.rail_movement_id ? (
                        <Badge
                          variant="secondary"
                          className="bg-blue-100 text-blue-800"
                        >
                          Por panel
                        </Badge>
                      ) : payment.is_verified === true ? (
                        <Badge
                          variant="secondary"
                          className="bg-green-100 text-green-800"
                        >
                          Verificado
                        </Badge>
                      ) : payment.is_verified === false ? (
                        <Badge
                          variant="secondary"
                          className="bg-yellow-100 text-yellow-800"
                        >
                          Pendiente
                        </Badge>
                      ) : (
                        <Badge
                          variant="secondary"
                          className="bg-gray-100 text-gray-600"
                        >
                          N/A
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {payment.image_url ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewPaymentProof(payment.image_url)}
                          className="h-6 px-2 text-xs text-blue-600 hover:text-blue-800"
                        >
                          <Eye className="mr-1 h-3 w-3" />
                          Ver comprobante
                        </Button>
                      ) : (
                        <span className="text-xs text-gray-400">
                          Sin comprobante
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center text-xs">
                      {payment.createdBy ? (
                        <span className="font-medium text-blue-600">
                          {getUserName(payment.createdBy)}
                        </span>
                      ) : (
                        <span className="text-gray-400">Sistema</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {canDeletePayments ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            handleDeletePayment(payment.id, payment.image_url)
                          }
                          disabled={deletingPaymentId === payment.id}
                          className="h-6 px-2 text-xs text-red-600 hover:bg-red-50 hover:text-red-800"
                          title="Eliminar pago"
                        >
                          {deletingPaymentId === payment.id ? (
                            <svg
                              className="h-3 w-3 animate-spin"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                          ) : (
                            <Trash2 className="h-3 w-3" />
                          )}
                        </Button>
                      ) : (
                        <span
                          className="text-xs text-gray-400"
                          title="Solo los super administradores pueden eliminar pagos"
                        >
                          —
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {remainingAmount > 0 && !showPaymentForm ? (
        <div className="mt-4 flex justify-center">
          <Button
            onClick={() => setShowPaymentForm(true)}
            variant="outline"
            className="flex items-center border-green-200 text-green-600 hover:bg-green-50 hover:text-green-700"
          >
            <PlusCircle className="mr-2 h-4 w-4" />
            Ingresar un pago
          </Button>
        </div>
      ) : null}

      {remainingAmount > 0 && showPaymentForm ? (
        <>
          <h2 className="mb-3 text-sm font-medium text-gray-700">
            Registrar nuevo pago
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="paymentAmount">Monto a pagar</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                    $
                  </span>
                  <Input
                    id="paymentAmount"
                    type="number"
                    placeholder="0"
                    value={paymentAmount || ""}
                    onChange={(e) =>
                      setPaymentAmount(parseFloat(e.target.value) || 0)
                    }
                    className="pl-7"
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Saldo pendiente: {formatCurrency(remainingAmount)}
                </p>
              </div>

              <div className="space-y-1">
                <Label htmlFor="paymentMethod">Método de pago</Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar método" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="transfer">
                      Transferencia bancaria
                    </SelectItem>
                    <SelectItem value="cash">Efectivo</SelectItem>
                    {!isSeller && <SelectItem value="check">Cheque</SelectItem>}
                    <SelectItem value="factoring">Factoring</SelectItem>
                    <SelectItem value="descuento_nomina">
                      Descuento nómina
                    </SelectItem>
                    <SelectItem value="rappel">Rappel</SelectItem>
                    <SelectItem value="other">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Bank selection for transfers */}
            {paymentMethod === "transfer" && (
              <div className="space-y-1">
                <Label htmlFor="transferBank">
                  Banco <span className="text-red-500">*</span>
                </Label>
                <Select value={bank} onValueChange={setBank}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar banco" />
                  </SelectTrigger>
                  <SelectContent>
                    {CHILEAN_BANKS.map((bankName) => (
                      <SelectItem key={bankName} value={bankName}>
                        {bankName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {paymentMethod !== "cash" && (
              <div className="space-y-1">
                <Label htmlFor="reference">Referencia (opcional)</Label>
                <Input
                  id="reference"
                  placeholder="Nº Transferencia, cheque, etc."
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                />
              </div>
            )}

            {/* Check-specific fields */}
            {paymentMethod === "check" && (
              <div className="space-y-4 rounded-lg border bg-gray-50 p-4">
                <h3 className="text-lg font-medium">Información del Cheque</h3>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-1">
                    <Label htmlFor="checkNumber">
                      Número de cheque <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="checkNumber"
                      placeholder="Ej: 123456"
                      value={checkNumber}
                      onChange={(e) => setCheckNumber(e.target.value)}
                    />
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="bank">
                      Banco <span className="text-red-500">*</span>
                    </Label>
                    <Select value={bank} onValueChange={setBank}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar banco" />
                      </SelectTrigger>
                      <SelectContent>
                        {CHILEAN_BANKS.map((bankName) => (
                          <SelectItem key={bankName} value={bankName}>
                            {bankName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-1">
                    <Label>
                      Fecha de recepción <span className="text-red-500">*</span>
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={`w-full justify-start text-left font-normal ${
                            !receptionDate && "text-muted-foreground"
                          }`}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {receptionDate ? (
                            format(receptionDate, "dd/MM/yyyy")
                          ) : (
                            <span>Seleccionar fecha</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={receptionDate}
                          onSelect={setReceptionDate}
                          initialFocus
                          locale={es}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="space-y-1">
                    <Label>
                      Fecha para cobrar <span className="text-red-500">*</span>
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={`w-full justify-start text-left font-normal ${
                            !collectionDate && "text-muted-foreground"
                          }`}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {collectionDate ? (
                            format(collectionDate, "dd/MM/yyyy")
                          ) : (
                            <span>Seleccionar fecha</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={collectionDate}
                          onSelect={setCollectionDate}
                          initialFocus
                          locale={es}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </div>
            )}

            {/* File Upload */}
            {paymentMethod !== "cash" && (
              <div className="space-y-1">
                <Label htmlFor="proofImage">Comprobante de pago *</Label>
                <div className="flex items-center space-x-2">
                  <Input
                    id="proofImage"
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/gif,application/pdf"
                    onChange={handleFileChange}
                    className="flex-1"
                  />
                </div>
                {filePreview && (
                  <div className="relative mt-2">
                    <img
                      src={filePreview}
                      alt="Vista previa"
                      className="h-28 rounded border bg-white object-contain p-1"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setFile(null);
                        setFilePreview(null);
                        // Clear the input field value
                        if (fileInputRef.current) {
                          fileInputRef.current.value = "";
                        }
                      }}
                      className="absolute right-1 top-1 rounded-full bg-white p-0.5 text-gray-500 shadow-sm hover:text-gray-800"
                    >
                      &times;
                    </button>
                  </div>
                )}
                <p className="text-xs text-gray-500">
                  Aceptamos JPG, PNG, GIF o PDF. Máximo 5MB.{" "}
                  <span className="text-red-500">*Requerido</span>
                </p>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowPaymentForm(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-green-600 hover:bg-green-700"
                disabled={
                  isSubmitting ||
                  fileUploading ||
                  paymentAmount <= 0 ||
                  (paymentMethod !== "cash" && !file)
                }
              >
                {isSubmitting || fileUploading ? (
                  <>
                    <svg
                      className="-ml-1 mr-2 h-4 w-4 animate-spin text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    {fileUploading
                      ? "Subiendo comprobante..."
                      : "Registrando pago..."}
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Registrar pago
                  </>
                )}
              </Button>
            </div>
          </form>
        </>
      ) : (
        remainingAmount <= 0 &&
        selectedOrder.paid && (
          <div className="rounded-md border border-green-200 bg-green-50 p-4">
            <p className="text-sm text-green-800">
              ¡El pago ha sido completado en su totalidad!
            </p>
          </div>
        )
      )}

      {/* Fallback for edge cases */}
      {remainingAmount === 0 && currentPayments.length === 0 && (
        <div className="py-4 text-center">
          <p className="mb-3 text-sm text-gray-500">
            {(selectedOrder.price_with_iva || selectedOrder.totalAmount) > 0
              ? "Aún no hay pagos registrados para esta nota de venta."
              : "Cargando información de pagos..."}
          </p>
          {(selectedOrder.price_with_iva || selectedOrder.totalAmount) > 0 && (
            <Button
              onClick={() => setShowPaymentForm(true)}
              variant="outline"
              className="border-green-200 text-green-600 hover:bg-green-50 hover:text-green-700"
            >
              <PlusCircle className="mr-2 h-4 w-4" />
              Registrar primer pago
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
